import type { JSX } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import type { ValidationReport } from '@profiler/shared'
import { ErrorPanel, LoadingPanel } from '../../components/States'
import { formatEstimate, formatNumber } from '../../format'

function Tile({
  label,
  value,
  tone = 'text.primary',
  hint,
}: Readonly<{
  label: string
  value: string
  tone?: string
  hint?: string
}>): JSX.Element {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: tone, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

function IssueTable({
  title,
  rows,
}: Readonly<{
  title: string
  rows: Array<{ row: number; accountName: string; reason: string }>
}>): JSX.Element | null {
  if (rows.length === 0) return null
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80, fontWeight: 600 }}>Row</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Account name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((issue) => (
              <TableRow key={`${issue.row}-${issue.reason}`}>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{issue.row}</TableCell>
                <TableCell>{issue.accountName}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{issue.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export function StepReview({
  report,
  isLoading,
  error,
  onRetry,
  creditBalance,
  onStart,
  isStarting,
  startError,
}: Readonly<{
  report: ValidationReport | undefined
  isLoading: boolean
  error: unknown
  onRetry: () => void
  creditBalance: number | null
  onStart: () => void
  isStarting: boolean
  startError: unknown
}>): JSX.Element {
  if (isLoading) return <LoadingPanel label="Validating the sheet" />
  if (error) return <ErrorPanel error={error} onRetry={onRetry} />
  if (!report) return <ErrorPanel error={new Error('No validation report was returned.')} onRetry={onRetry} />

  // The p90 is the number worth checking against the balance: a job that stops
  // halfway through on an exhausted balance has spent credits for a partial list.
  const overBalance = creditBalance !== null && report.estimate.p90 > creditBalance
  const nothingToDo = report.usable === 0

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile label="Rows in the file" value={formatNumber(report.rowCount)} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile label="Usable" value={formatNumber(report.usable)} tone="success.main" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile
            label="Blocked"
            value={formatNumber(report.blocked.length)}
            tone={report.blocked.length > 0 ? 'error.main' : 'text.primary'}
            hint="Excluded from the run"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile
            label="Free from earlier runs"
            value={formatNumber(report.reusable)}
            tone="success.main"
            hint="Already searched with this preset; replayed at zero credits"
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            Credit estimate
          </Typography>
          <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatEstimate(report.estimate)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mean with a P10 to P90 band, driven by the measured yield distribution rather than by quota times
            unit cost.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontVariantNumeric: 'tabular-nums' }}>
            Live balance: {creditBalance === null ? 'unavailable' : formatNumber(creditBalance)}
          </Typography>
        </CardContent>
      </Card>

      {overBalance ? (
        <Alert severity="warning">
          <AlertTitle>The high end of the estimate exceeds your balance</AlertTitle>A worst case of{' '}
          {formatNumber(report.estimate.p90)} credits against a balance of {formatNumber(creditBalance ?? 0)}.
          The job will pause at the cap rather than fail, but it may finish only part of the list.
        </Alert>
      ) : null}

      {report.duplicates.length > 0 ? (
        <Alert severity="info">
          <AlertTitle>{report.duplicates.length} duplicate domains in the file</AlertTitle>
          Each is profiled once and every input row still gets an export row.
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {report.duplicates.slice(0, 8).map((duplicate) => (
              <li key={duplicate.domain}>
                <code>{duplicate.domain}</code> on rows {duplicate.rows.join(', ')}
              </li>
            ))}
          </Box>
        </Alert>
      ) : null}

      <IssueTable title={`Blocked rows (${report.blocked.length})`} rows={report.blocked} />
      <IssueTable title={`Warnings (${report.warnings.length})`} rows={report.warnings} />

      {nothingToDo ? (
        <Alert severity="error">
          No usable rows. Go back to the mapping step and check that the company name column is correct.
        </Alert>
      ) : null}

      {startError ? <ErrorPanel error={startError} /> : null}

      <Box>
        <Button
          size="large"
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={nothingToDo || isStarting}
          onClick={onStart}
        >
          {isStarting ? 'Starting' : `Start job (${formatNumber(report.usable)} companies)`}
        </Button>
      </Box>
    </Stack>
  )
}
